<script>
import i18n from '@/plugins/i18n';
import { mapActions } from 'vuex';
import ListViewItems from './page-list-view-items.vue';
import GridViewItems from './page-grid-view-items.vue';

export default {
    components: {
        ListViewItems,
        GridViewItems,
    },

    data() {
        return {
            search_phrase: '',
            search_in_progress: false,

            view_type: 'list',
            
            directories: [],
            documents: [],

            currentPath: [],

            showContextMenuTip: false,
        }
    },

    computed: {
        DOCUMENTS() {
            const relevant_data = this.currentPath.length > 0 ? this.documents : this.directories;
            if (!this.search_phrase) {
                return relevant_data;
            } else {
                return relevant_data.filter(x => x.name.toLowerCase().includes(this.search_phrase.toLowerCase()));
            }
        },

        TOP_BAR_VIEW_ITEMS() {
            return [
                {
                    key: 'grid',
                    icon: 'fa-solid fa-grip',
                    tooltip: i18n.t('documents.grid_view'),
                },
                {
                    key: 'list',
                    icon: 'fa-solid fa-bars',
                    tooltip: i18n.t('documents.list_view'),
                },
            ];
        },

        TOP_BAR_INTERACTION_ITEMS() {
            return [
                {
                    key: 'upload_document',
                    icon: 'fa-solid fa-file-arrow-up',
                    tooltip: i18n.t('documents.upload_document'),
                },
            ];
        },
    },

    mounted() {
        this._getDirectories();
        this.SetupContextMenuTip();
    },

    methods: {
        ...mapActions('documents', [
            'getDirectories',
            'getDocuments',
            'uploadDocument',
            'updateDocument',
            'updateDocumentRelation',
            'deleteDocument',
            'viewDocument',
        ]),

        _getDirectories() {
            this.getDirectories()
                .then(res => {
                    this.directories = res.data.value;
                })
                .catch(({ response }) => {
                    this.$notify('error', response.data.messageName);
                })
        },

        _getDocuments() {
            let params = {
                key: 'path',
                value: this.currentPath.map(x => x.id).join(''),
                category: null,
            };

            this.getDocuments(params)
                .then(res => {
                    this.documents = res.data.value;
                })
        },

        _uploadDocument(file) {
            let _path = this.currentPath.map(x => x.id);

            if (this.currentPath.length === 0) {
                _path = [4];
                this.$notify('info', i18n.t('documents.warnings.will_be_in_my_documents'), true);
            }

            const params = {
                body: { 
                    path: _path,
                },
                document: file,
            };

            this.uploadDocument(params)
                .then(res => {
                    this.$notify('success', res.data.messageName);

                    res.data.value.id = Number(res.data.value.id);
                    res.data.value['size'] = file.size;
                    res.data.value['type'] = file.name.split('.')[1];
                    res.data.value['created_by_name'] = localStorage.getItem('firstname') + ' ' + localStorage.getItem('surname');
                    this.documents.push(res.data.value);
                })
                .catch(({ response }) => {
                    this.$notify('error', response.data.messageName);
                })
        },

        _updateDocument(id, key, value) {
            const params = {
                id: id,
                key: key,
                value: value,
            };

            this.updateDocument(params)
                .then(res => {
                    this.$notify('success', res.data.messageName);
                })
                .catch(({ response }) => {
                    this.$notify('error', response.data.messageName);
                })
        },

        _deleteDocument(document, action) {
            const params = {
                id: document.id,
                action: action
            };

            this.deleteDocument(params)
                .then(res => {
                    this.$notify('success', res.data.messageName);
                    this.documents = this.documents.filter(x => x.id != document.id);
                })
                .catch(({ response }) => {
                    this.$notify('error', response.data.messageName);
                })
        },

        _viewDocument(document) {
            if (document.is_directory) {
                this.currentPath.push(document);
                this._getDocuments();
            } else {
                this.viewDocument(document.id);
            }
        },

        HandleFileUpload(e) {
            try {
                let file;
                if (!e.target || !e.target.files || e.target.files.length === 0)
                    file = null;

                file = e.target.files[0];
                if (!file) {
                    this.$notify('info', i18n.t('kpisMetrics.should_add_document'));
                }
    
                if (file)
                    this._uploadDocument(file);
                
                e.target.value = null;
            } catch (error) {
                this.$notify('info', i18n.t('kpisMetrics.should_add_document'));
            }
        },

        ClickFileUpload() {
            this.$refs.file_input.click();
        },

        SetupContextMenuTip() {
            const _showContextMenuTooltip = sessionStorage.getItem('show_documents_context_menu_tooltip');
            if (_showContextMenuTooltip) {
                this.SetContextMenuTip(_showContextMenuTooltip);
            } else {
                this.SetContextMenuTip('true');
            }
        },
        
        SetContextMenuTip(bool) {
            this.showContextMenuTip = bool;
            sessionStorage.setItem('show_documents_context_menu_tooltip', bool);
        },

        HandleSearch(reset = false) {
            const el = document.getElementById('search-input');
            if (el) {
                this.search_in_progress = true;
                setTimeout(() => {
                    if (!reset) {
                        this.search_phrase = el.value;
                    } else {
                        this.search_phrase = '';
                        el.value = '';
                    }
                    this.search_in_progress = false;
                }, 100);
            }
        },

        HandleClickPath(path) {
            if (!path) {
                this.currentPath = [];
                return;
            }

            const index = this.currentPath.findIndex(x => x.id === path.id);
            if (index === this.currentPath.length -1) {
                return;
            } else {
                this.currentPath.splice(index, this.currentPath.length - (index + 1));
            }

            this._getDocuments();
        },
    }
}
</script>

<template>
    <div class="documentation-wrapper">
        <div class="path-bar">
            <button @click="HandleClickPath(null)">
                {{ $t('documents.title') }}
            </button>
            <button v-for="item in currentPath" :key="item.id"
                @click="HandleClickPath(item)"
            >
                /&nbsp;&nbsp;{{ item.name }}
            </button>
        </div>
        <div class="top-bar">
            <div class="top-bar-left">
                <input class="sust-input-field" type="text" maxlength="150"
                    id="search-input"
                    :placeholder="$t('documents.search_placeholder')"
                    @keyup.enter="HandleSearch()"
                >
                <button class="icon-button" @click="HandleSearch()" v-if="!search_in_progress">
                    <i class="fa-solid fa-magnifying-glass"></i>
                </button>
                <loading-spinner v-else />
                <button class="icon-button" @click="HandleSearch(true)" v-if="search_phrase != '' && !search_in_progress">
                    <i class="fa-solid fa-rotate-left"></i>
                </button>
            </div>

            <div class="top-bar-right">
                <button v-for="item in TOP_BAR_INTERACTION_ITEMS" :key="item.key" class="icon-button"
                    v-sust-tooltip="{text: item.tooltip}"
                    @click="() => ClickFileUpload()">
                    <i v-if="item.icon" :class="item.icon"></i>
                </button>
                <button v-for="item in TOP_BAR_VIEW_ITEMS" :key="item.key" class="icon-button"
                    v-sust-tooltip="{text: item.tooltip}" :class="{'selected-view-type': view_type === item.key}"
                    @click="() => view_type = item.key">
                    <i v-if="item.icon" :class="item.icon"></i>
                </button>
            </div>
        </div>

        <div v-if="showContextMenuTip == 'true'" 
            class="context-menu-tip"
            @click="SetContextMenuTip('false')"
        >
            <i class="fa-solid fa-lightbulb"></i>
            <div>
                {{ $t('documents.context_menu_tip') }}
            </div>
        </div>

        <div class="flex-all-centered-row" style="border-bottom: thin solid lightgray; margin: .5em 0;"></div>

        <div v-if="view_type === 'list'" style="width: 100%;">
            <list-view-items 
                :Items="DOCUMENTS" 
                :IsLinked="true" 
                @upload="ClickFileUpload()"
                @delete="_deleteDocument($event, 'delete')"
                @erase="_deleteDocument($event, 'erase')"
                @view="_viewDocument($event)"
            />
        </div>

        <div v-else-if="view_type === 'grid'" style="width: 100%;">
            <grid-view-items 
                :Items="DOCUMENTS" 
                :IsLinked="true" 
                @upload="ClickFileUpload()"
                @delete="_deleteDocument($event, 'delete')"
                @erase="_deleteDocument($event, 'erase')"
                @view="_viewDocument($event)"
            />
        </div>

        <input type="file" v-show="false" ref="file_input" 
            accept=".pdf, .docx, .doc, .jpg, .png, .xls, .xlsx, .csv" 
            @change="HandleFileUpload($event)"
        >
    </div>
</template>

<style lang='scss' scoped>
.documentation-wrapper {
    background-color: whitesmoke;
    border-radius: .5em;
    box-shadow: 0 .1em .5em .1em gray;
    padding: .5em;
    width: 100%;
    height: 100%;
    justify-self: center !important;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;

    > .path-bar {
        width: 100%;
        display: flex;
        flex-direction: row;
        place-items: flex-start;
        text-align: left;

        > button {
            background-color: transparent;
            color: gray;
            outline: 0;
            border: 0;
            padding: .25em;

            cursor: pointer;
            transition: color 250ms;

            &:focus {
                color: black;
                font-weight: bold;
                text-decoration: underline;
                text-underline-offset: .25em;
            }
            
            &:hover {
                color: black;
                font-weight: bold;
                text-decoration: underline;
                text-underline-offset: .25em;
            }
        }
    }

    > .top-bar {
        width: 100%;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;

        > .top-bar-left {
            display: flex;
            flex-direction: row;
            justify-content: flex-start;
            align-items: center;
            width: 50%;

            > input {
                transition: outline 250ms;
                width: 100%;
                margin-right: .5em;
                padding: .35vw;
                font-size: .9vw;
                color: gray;

                &::placeholder {
                    color: darkgray !important;
                }
            }

            > button {
                font-size: 1vw;
                transform-origin: 50%, 50%;
                transition: color 250ms, scale 100ms !important;
                &:hover {
                    color: black !important;
                    scale: 1.1 !important;
                }
            }
        }
        
        > .top-bar-right {
            display: flex;
            flex-direction: row;
            justify-content: flex-end;
            align-items: center;

            > button {
                width: 2.5vw !important;
                height: 2.5vw !important;
                padding: .25em !important;
                font-weight: bold !important;
                font-size: 1.5vw !important;
                border-radius: 1000px !important;
            }

            > .selected-view-type {
                background-color: rgba(0, 0, 0, .1) !important;
            }
        }
    }
}

.no-border {
    border: 0 !important;
}

.icon-button {
    font-size: 1vw;
    transition: color 250ms !important;

    > .fa-square-plus {
        color: var(--sustainfinity-green) !important;
    }

    > .fa-square-minus {
        color: var(--invalid-input) !important;
    }

    &:hover {
        > .fa-square-plus {
            color: var(--sustainfinity-green--hover) !important;
        }

        > .fa-square-minus {
            color: var(--invalid-input--hover) !important;
        }
    }
}

.context-menu-tip {
    border-radius: .25em;
    border: thin solid rgb(215, 215, 215);

    margin-top: .5em;
    padding: .5em .25em;
    align-self: flex-start;
    width: fit-content;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    
    cursor: pointer;
    transition: opacity 0.2s;
    &:hover {
        opacity: 40%;
    }

    > * {
        margin-right: .25em;
    }

    > i {
        color: var(--sustainfinity-yellow);
    }
} 
</style>